import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaClipboardList,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaEdit,
  FaEye,
  FaChartBar,
  FaCalendarAlt,
  FaCog,
  FaUserEdit,
  FaBookOpen,
  FaListAlt,
  FaUsers,
  FaCopy,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import MdlSelectSession from "../dialog/MdlSelectSession";
import MdlEditExam from "../dialog/MdlEditExam";
import MdlUpdateStatus from "../dialog/MdlUpdateStatus";
import MdlCopyAndCreateExam from "../dialog/MdlCopyAndCreateExam";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const EXAM_STATUS = {
  UPCOMING: "1",
  LIVE: "2",
  OVER: "3",
};

//Created By Cursor
function normalizeExamType(value) {
  const v = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (v === "CLASS_TEST" || v === "CLASSTEST") return "CLASS_TEST";
  return "EXAM";
}

const QUICK_ACTIONS = [
  {
    path: "ExamPaperSettings",
    label: "Paper Settings",
    icon: FaCog,
    tone: "blue",
  },
  {
    path: "ExamAddMarkSubjectWise",
    label: "Mark-Subject Wise",
    icon: FaBookOpen,
    tone: "teal",
  },
  {
    path: "ExamAddMarkStudentWise",
    label: "Mark-Student Wise",
    icon: FaUserEdit,
    tone: "cyan",
  },
  {
    path: "ExamMarkingSystem",
    label: "View Marks",
    icon: FaChartBar,
    tone: "indigo",
  },
  {
    path: "ExamDateSheet",
    label: "Date Sheet",
    icon: FaCalendarAlt,
    tone: "orange",
  },
  {
    path: "ExamGuideline",
    label: "Guideline",
    icon: FaListAlt,
    tone: "green",
  },
];

function getParticipatingClasses(item) {
  const raw = item?.participating_class_list;
  if (!raw) return { count: 0, names: [] };

  if (Array.isArray(raw)) {
    const names = raw
      .map((classItem) => {
        if (typeof classItem === "object" && classItem !== null) {
          const classPart =
            classItem.class_name ||
            classItem.name ||
            classItem.class_section_name ||
            classItem.class_id ||
            "";
          const sectionPart =
            classItem.section_name || classItem.section_id || "";
          if (classPart && sectionPart) return `${classPart} - ${sectionPart}`;
          return (
            classPart ||
            sectionPart ||
            "Unknown Class"
          );
        }
        return String(classItem);
      })
      .map((n) => String(n || "").trim())
      .filter(Boolean);
    return { count: names.length, names };
  }

  if (typeof raw === "string") {
    const names = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return { count: names.length, names };
  }

  return { count: 0, names: [] };
}

function statusTone(status) {
  if (status === EXAM_STATUS.UPCOMING) return "upcoming";
  if (status === EXAM_STATUS.LIVE) return "live";
  return "over";
}

const ExamCard = memo(function ExamCard({
  exam,
  onEdit,
  onStatus,
  isClassTest = false,
}) {
  const classes = getParticipatingClasses(exam);
  const tone = statusTone(exam.status);
  const baseParams = `${exam.exam_id}/${exam.session_id}/${encodeURIComponent(
    exam.exam_name || ""
  )}/${encodeURIComponent(exam.status_name || "")}`;
  const nameLabel = isClassTest ? "Class Test Name:" : "Exam Name:";
  const untitled = isClassTest ? "Untitled class test" : "Untitled exam";
  const editLabel = isClassTest ? "Edit Class Test" : "Edit Exam";
  const detailsLabel = isClassTest ? "Class Test Details" : "Exam Details";

  return (
    <article className={`el-card el-card--${tone}`}>
      <div className="el-card__top">
        <div className="el-card__name-row">
          <h3 className={`el-name-chip el-name-chip--${tone}`}>
            <span className="el-name-chip__label">{nameLabel}</span>
            <span className="el-name-chip__value">
              {exam.exam_name || untitled}
            </span>
          </h3>
          {exam.exam_type ? (
            <span className="el-exam-type" title="Exam Type">
              <span className="el-exam-type__label">Exam Type:</span>
              <span className="el-exam-type__value">{exam.exam_type}</span>
            </span>
          ) : null}
          <span className={`el-status el-status--${tone}`}>
            {exam.status_name || "—"}
          </span>
        </div>
        <div className="el-card__actions">
          <button
            type="button"
            className="el-btn el-btn--edit"
            onClick={() => onEdit(exam)}
          >
            <FaEdit aria-hidden /> {editLabel}
          </button>
          <Link
            to={`/AdminMain/ExamDetails/${baseParams}`}
            className="el-btn el-btn--details"
          >
            <FaEye aria-hidden /> {detailsLabel}
          </Link>
          <button
            type="button"
            className="el-btn el-btn--status"
            onClick={() => onStatus(exam)}
          >
            <FaSyncAlt aria-hidden /> Status
          </button>
          {exam.status === EXAM_STATUS.OVER ? (
            <Link
              to={`/AdminMain/ExamClassResultStatus/${exam.exam_id}/${exam.session_id}/${encodeURIComponent(
                exam.exam_name || ""
              )}/${exam.start_date || ""}`}
              className="el-btn el-btn--result"
            >
              <FaChartBar aria-hidden /> Result
            </Link>
          ) : null}
        </div>
      </div>

      <div className="el-card__meta">
        <div className="el-class-chips" aria-label="Participating classes">
          <span className="el-class-chips__count" title="Total Class">
            <FaUsers aria-hidden />
            <span className="el-class-chips__count-label">Total Class</span>
            <strong>{classes.count}</strong>
          </span>
          {classes.names.length > 0 ? (
            classes.names.map((name, idx) => (
              <span key={`${name}-${idx}`} className="el-class-chip" title={name}>
                {name}
              </span>
            ))
          ) : (
            <span className="el-class-chip el-class-chip--empty">
              No classes assigned
            </span>
          )}
        </div>
        {exam.exam_remark ? (
          <span className="el-remark-chip" title={exam.exam_remark}>
            <span className="el-remark-chip__label">Remark:</span>
            <span className="el-remark-chip__value">{exam.exam_remark}</span>
          </span>
        ) : null}
      </div>

      <div className="el-quick">
        <div className="el-quick__grid">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.path}
                to={`/AdminMain/${action.path}/${baseParams}`}
                className={`el-quick__link el-quick__link--${action.tone}`}
                title={action.label}
              >
                <span className="el-quick__icon" aria-hidden>
                  <Icon />
                </span>
                <span className="el-quick__label">{action.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </article>
  );
});

export function ExamTypeList({ recordType = "EXAM" }) {
  const loggedIn = localStorage.getItem("token") !== null;
  const location = useLocation();
  const isClassTest = recordType === "CLASS_TEST";
  const listPath = isClassTest
    ? "/AdminMain/ClassTestList"
    : "/AdminMain/ExamList";
  const listPageTitle = isClassTest ? "Class Test List" : "Exam List";
  const listPageSubtitle = isClassTest
    ? "Manage class tests, marks, and datesheets"
    : "Manage exams, marks, datesheets, and admit cards";
  const createPath = isClassTest
    ? "/AdminMain/ExamCreation/?type=CLASS_TEST"
    : "/AdminMain/ExamCreation/";
  const createLabel = isClassTest ? "Create Class Test" : "Create Exam";
  const copyLabel = isClassTest
    ? "Copy And Create Class Test"
    : "Copy And Create Exam";
  const searchPlaceholder = isClassTest
    ? "Search class tests…"
    : "Search exams…";
  const emptyTitle = isClassTest ? "No class tests found" : "No exams found";
  const emptyHint = isClassTest
    ? "Create your first class test for this session."
    : "Create your first exam for this session.";

  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [examList, setExamList] = useState([]);
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isCopyCreateOpen, setIsCopyCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  //Created By Cursor
  const getExamList = useCallback(async (session_id, { soft = false } = {}) => {
    if (!session_id) {
      setExamList([]);
      setBootLoading(false);
      setRefreshing(false);
      return;
    }

    if (soft) setRefreshing(true);
    else setBootLoading(true);

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setExamList([]);
      myToast.notify_warning("School code is missing.");
      setBootLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE()}getExamList.php`, {
        params: {
          school_code: schoolCode,
          session_id,
          _ts: Date.now(),
        },
        headers: { "Cache-Control": "no-cache" },
      });
      if (response.data?.api_status === "OK") {
        setExamList(response.data.data || []);
      } else {
        setExamList([]);
        myToast.notify_warning(
          response.data?.api_remark || "Could not load exams."
        );
      }
    } catch (error) {
      console.error("Error fetching exam list:", error);
      setExamList([]);
      myToast.notify_warning("Network error loading exams.");
    } finally {
      setBootLoading(false);
      setRefreshing(false);
    }
  }, []);

  //Created By Cursor
  const loadCurrentSessionAndList = useCallback(
    (opts = {}) => {
      try {
        const mSession = SessionManager.getCurrentSession();
        const sid = mSession?.[0]?.session_id || "";
        const sname = mSession?.[0]?.session_name || "";
        const sstatus = mSession?.[0]?.status || "";
        setSessionId(sid);
        setSessionName(sname);
        setSessionStatus(sstatus);
        getExamList(sid, opts);
      } catch (e) {
        console.error(e);
        setBootLoading(false);
        myToast.notify_warning("Could not load current session.");
      }
    },
    [getExamList]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    loadCurrentSessionAndList();
  }, [loadCurrentSessionAndList]);

  //Created By Cursor — refresh after returning from ExamCreation (dialog / Link / back)
  useEffect(() => {
    if (location.pathname !== listPath) return;
    if (!location.state?.refreshExamList) return;
    loadCurrentSessionAndList({ soft: true });
  }, [location.pathname, location.state, listPath, loadCurrentSessionAndList]);

  const handleSessionDialogToggle = () => {
    setIsOpenSessionDialog((prev) => !prev);
  };

  const handleSelectedSession = (session_id, session_name, session_status) => {
    setSessionId(session_id);
    setSessionName(session_name);
    setSessionStatus(session_status || "");
    setIsOpenSessionDialog(false);
    getExamList(session_id);
  };

  const handleEditClick = (exam) => {
    setSelectedExam(exam);
    setIsEditModalOpen(true);
  };

  const handleEditSave = (SessionId) => {
    setIsEditModalOpen(false);
    getExamList(SessionId || sessionId, { soft: true });
  };

  const handleUpdateStatusOpen = (exam) => {
    setSelectedExam(exam);
    setIsUpdateStatusOpen(true);
  };

  const handleCloseModals = (SessionId) => {
    setIsUpdateStatusOpen(false);
    setSelectedExam(null);
    getExamList(SessionId || sessionId, { soft: true });
  };

  const visibleExams = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch = (exam) => {
      if (!q) return true;
      const hay = [
        exam.exam_name,
        exam.exam_type,
        exam.exam_remark,
        exam.status_name,
        exam.exam_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    };

    return examList
      .filter((exam) => normalizeExamType(exam.exam_type) === recordType)
      .filter(matchesSearch)
      .sort((a, b) =>
        String(a.exam_name || "").localeCompare(
          String(b.exam_name || ""),
          undefined,
          { sensitivity: "base" }
        )
      );
  }, [examList, searchTerm, recordType]);

  const typeCount = useMemo(
    () =>
      examList.filter(
        (exam) => normalizeExamType(exam.exam_type) === recordType
      ).length,
    [examList, recordType]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (bootLoading) {
    return <MyLoader />;
  }

  return (
    <div
      className="content payroll-page sm-page el-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> {listPageTitle}</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">{listPageTitle}</h1>
              <p className="sm-hero__sub">{listPageSubtitle}</p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <button
              type="button"
              className="el-session-pill"
              onClick={handleSessionDialogToggle}
              title="Change session"
            >
              <span className="el-session-pill__label">Session:</span>
              <span className="el-session-pill__name">
                {sessionName || "Select"}
              </span>
              {sessionStatus ? (
                <span
                  className={`el-session-pill__status${
                    String(sessionStatus).toLowerCase() === "active" ||
                    sessionStatus === "1"
                      ? " is-active"
                      : ""
                  }`}
                >
                  {sessionStatus}
                </span>
              ) : null}
            </button>
            <div className="sm-hero__search-group">
              <FaSearch className="el-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label={searchPlaceholder}
              />
            </div>
            <button
              type="button"
              onClick={() => getExamList(sessionId, { soft: true })}
              disabled={refreshing || !sessionId}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh list"
              aria-label="Refresh list"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="el-hero-link"
              onClick={() => setIsCopyCreateOpen(true)}
              disabled={!sessionId}
              title={copyLabel}
            >
              <FaCopy aria-hidden /> {copyLabel}
            </button>
            <Link
              to={createPath}
              className="el-hero-link el-hero-link--solid"
            >
              <FaPlus aria-hidden /> {createLabel}
            </Link>
          </div>
        </header>

        <div className="el-content" aria-label={listPageTitle}>
          {visibleExams.length === 0 ? (
            <div className="sm-empty el-empty">
              <div className="el-empty__circle" aria-hidden>
                <FaClipboardList />
              </div>
              <p className="sm-empty__title">
                {typeCount === 0 ? emptyTitle : "No matches for your search"}
              </p>
              <p className="sm-empty__hint">
                {typeCount === 0
                  ? emptyHint
                  : "Try a different name or status."}
              </p>
              {typeCount === 0 ? (
                <Link
                  to={createPath}
                  className="el-btn el-btn--primary"
                  style={{ marginTop: 12 }}
                >
                  <FaPlus aria-hidden /> {createLabel}
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="el-list">
              {visibleExams.map((exam, idx) => (
                <ExamCard
                  key={exam.exam_id || `${recordType}-${idx}`}
                  exam={exam}
                  onEdit={handleEditClick}
                  onStatus={handleUpdateStatusOpen}
                  isClassTest={isClassTest}
                />
              ))}
            </div>
          )}

          <div className="el-footer-cta">
            <Link to={createPath} className="el-btn el-btn--primary el-btn--wide">
              <FaPlus aria-hidden /> {createLabel}
            </Link>
          </div>
        </div>
      </div>

      {isUpdateStatusOpen ? (
        <MdlUpdateStatus item={selectedExam} onClose={handleCloseModals} />
      ) : null}
      {isEditModalOpen && selectedExam ? (
        <MdlEditExam
          onClose={() => setIsEditModalOpen(false)}
          examName={selectedExam}
          onSave={handleEditSave}
        />
      ) : null}
      {isCopyCreateOpen ? (
        <MdlCopyAndCreateExam
          targetSessionId={sessionId}
          targetSessionName={sessionName}
          onClose={() => setIsCopyCreateOpen(false)}
          onSuccess={(sid) => getExamList(sid || sessionId, { soft: true })}
        />
      ) : null}
      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={handleSessionDialogToggle}
          action2={handleSelectedSession}
        />
      ) : null}
    </div>
  );
}

export default function ExamList() {
  return <ExamTypeList recordType="EXAM" />;
}
